import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { ProductDisplayBox } from '../components/ProductDisplayBox';
import { HomeHeroSection } from '../components/HomeHeroSection';
import { HomeSearchSection } from '../components/HomeSearchSection';
import { HomeFeaturedProducts } from '../components/HomeFeaturedProducts';
import { HomeBenefitsSection } from '../components/HomeBenefitsSection';
import { BrandCarousel } from '../components/BrandCarousel';
import { GoogleReviewsCarousel } from '../components/GoogleReviewsCarousel';
import { useProducts } from '../hooks/useProducts';

export function HomePage() {
  const { products, isLoading, isError } = useProducts();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredProducts = useMemo(() => {
    if (!debouncedQuery.trim()) {
      return products.slice(0, 6);
    }

    const query = debouncedQuery.toLowerCase();
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query) ||
        product.brand.toLowerCase().includes(query) ||
        product.type.toLowerCase().includes(query)
    );
  }, [products, debouncedQuery]);

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="mx-auto max-w-md px-4 py-8 text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Error Loading Products</h2>
          <p className="mb-4 text-gray-600">Unable to load products. Please try again later.</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <HomeHeroSection />

      <HomeSearchSection
        searchQuery={searchQuery}
        resultCount={filteredProducts.length}
        onSearchInput={handleSearchInput}
        onSubmit={handleSearchSubmit}
      />

      <BrandCarousel />

      <section className="bg-slate-50 py-16 sm:py-20">
        <div className="container mx-auto px-4">
          {searchQuery ? (
            <>
              <div className="mb-10">
                <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
                  Search Results for “{searchQuery}”
                </h2>
                <p className="mt-3 max-w-xl text-base leading-7 text-slate-600 sm:text-lg">
                  Browse the appliances that match your search.
                </p>
              </div>

              {filteredProducts.length > 0 ? (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                  {filteredProducts.map((product) => (
                    <ProductDisplayBox key={product.id} product={product} variant="default" />
                  ))}
                </div>
              ) : (
                <div className="rounded-3xl border border-slate-200 bg-white py-12 text-center shadow-sm">
                  <AlertCircle className="mx-auto mb-4 h-12 w-12 text-slate-400" />
                  <h3 className="mb-2 text-lg font-semibold text-slate-900">No products found</h3>
                  <p className="mb-4 text-slate-500">
                    Try adjusting your search terms or browse all products.
                  </p>
                  <Link
                    to="/shop"
                    className="inline-flex items-center rounded-full bg-blue-700 px-5 py-3 text-sm font-bold text-white shadow-sm hover:bg-blue-600"
                  >
                    Browse All Products
                  </Link>
                </div>
              )}

              {filteredProducts.length > 0 && (
                <div className="mt-8 text-center">
                  <Link
                    to={`/shop?search=${encodeURIComponent(searchQuery)}`}
                    className="inline-flex items-center gap-2 rounded-full bg-blue-700 px-6 py-3 text-base font-bold text-white shadow-sm transition hover:bg-blue-600"
                  >
                    View All {filteredProducts.length} Results
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <HomeFeaturedProducts products={filteredProducts} />
          )}
        </div>
      </section>

      <HomeBenefitsSection />

      <section className="bg-gray-900 py-16 text-white">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <Shield className="mx-auto mb-6 h-16 w-16 text-yellow-400" />
            <h2 className="section-title mb-6 text-4xl font-bold">Quality You Can Trust</h2>
            <p className="section-subtext mb-8 text-xl text-gray-300">
              Every appliance in our inventory is thoroughly tested and certified for reliability.
            </p>
            <Link
              to="/shop"
              className="inline-block rounded-lg bg-yellow-400 px-8 py-4 text-xl font-bold text-black transition hover:bg-yellow-300"
            >
              Browse Selection
            </Link>
          </div>
        </div>
      </section>

      <GoogleReviewsCarousel />
    </div>
  );
}
